/**
 * 候选召回页的只读结构块（后台对齐批 4；摸底 C_应答配置 第 7 条、D_规则字段配置 第 14 条）：
 * 数据状态、排序（按生效引擎显示）与候选呈现分层、指南召回五种来源、知识推送与约束、召回评测入口。
 * 数据全取 `GET /recall/schema`（运行时单源：facts/states.py、业务包、生效画像、生效配置、jev.py、knowledge.py），不手抄；
 * 规则编号都能跳「运行规则」页对应那条。
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { LoadingInline } from "@/components/ui/feedback";
import {
	type RecallGuideSourceKey,
	type RecallSchema,
	type RecallStateRow,
	planErrorMessage,
	useRecallSchema,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { STATE_LABEL, labelOf } from "./recall-labels";

const linkCls = "text-brand-accent hover:underline";

/** 规则编号：点了去「运行规则」页并打开那条 */
export function RuleLink({ id, names }: { id: string; names?: Record<string, string> }) {
	return (
		<Link
			to="/plan-lab"
			search={{ rule: id }}
			hash="guards"
			title={names?.[id] ? `${id} ${names[id]}（去运行规则看）` : `去运行规则看 ${id}`}
			onClick={(e) => e.stopPropagation()}
			className="font-mono text-xs text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
			data-testid="plan-recall-rule-link"
		>
			{id}
		</Link>
	);
}

function RuleLinks({
	ids,
	names,
	nowrap = false,
}: {
	ids: string[];
	names?: Record<string, string>;
	nowrap?: boolean;
}) {
	if (!ids.length) return <span className="text-text-muted">—</span>;
	return (
		<span className={nowrap ? "inline-flex gap-x-2" : "inline-flex flex-wrap gap-x-2"}>
			{ids.map((r) => (
				<RuleLink key={r} id={r} names={names} />
			))}
		</span>
	);
}

/** 拿结构：三态（加载 ／ 失败可重试 ／ 有数据） */
function WithSchema({
	testId,
	children,
}: {
	testId: string;
	children: (s: RecallSchema) => React.ReactNode;
}) {
	const q = useRecallSchema();
	if (q.isLoading)
		return (
			<Card data-testid={testId}>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !q.data)
		return (
			<Card data-testid={testId}>
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-recall-schema-fail"
					/>
				</CardContent>
			</Card>
		);
	return <div data-testid={testId}>{children(q.data)}</div>;
}

function KV({ k, children }: { k: string; children: React.ReactNode }) {
	return (
		<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
			<span className="w-28 shrink-0 text-xs text-text-muted">{k}</span>
			<span className="min-w-0 flex-1 text-sm text-text-primary">{children}</span>
		</div>
	);
}

function Pre({ text, testId }: { text: string; testId?: string }) {
	return (
		<pre
			className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-surface-border bg-surface-muted p-3 font-mono text-xs text-text-primary"
			data-testid={testId}
		>
			{text || "（空）"}
		</pre>
	);
}

/* ── 数据状态 ─────────────────────────────────────────────── */

const USE_LABEL: Record<keyof RecallStateRow["used_by"], string> = {
	recall: "召回数据路（工具画像「适用的数据状态」）",
	admission: "准入 ＋ 执行前置（工具画像「前置条件」）",
	knowledge: "知识推送（知识条目限定的数据状态）",
	guides: "指南条件（指南「数据条件」）",
};

export function DataStatesPanel() {
	const [open, setOpen] = useState<RecallStateRow | null>(null);
	return (
		<WithSchema testId="plan-block-recall-states">
			{(s) => {
				const cols: PlanColumn<RecallStateRow>[] = [
					{
						key: "key",
						title: "数据状态",
						className: "max-w-[230px]",
						render: (r) => (
							<span className="flex items-center gap-1.5" title={r.key}>
								<span className="truncate">{labelOf(STATE_LABEL, r.key)}</span>
								{!r.produced ? (
									<Badge tone="muted">取数不产出</Badge>
								) : !r.in_enum ? (
									<Badge tone="warning">不在画像枚举</Badge>
								) : null}
							</span>
						),
					},
					{ key: "scope", title: "算在", className: "w-14", render: (r) => r.scope },
					{
						key: "fields",
						title: "来自字段",
						className: "max-w-[230px]",
						render: (r) => <CellTags mono max={1} items={r.fields} />,
					},
					{
						key: "how",
						title: "怎么算",
						className: "max-w-[280px]",
						render: (r) => (
							<span className="block truncate text-text-secondary" title={r.how}>
								{r.how}
							</span>
						),
					},
					{
						key: "use",
						title: "召回 · 准入 · 知识 · 指南",
						className: "whitespace-nowrap",
						render: (r) => (
							<span className="font-mono text-xs text-text-secondary">
								{r.used_by.recall.length} · {r.used_by.admission.length} ·{" "}
								{r.used_by.knowledge.length} · {r.used_by.guides.length}
							</span>
						),
					},
					{
						key: "rules",
						title: "规则",
						className: "whitespace-nowrap",
						render: (r) => <RuleLinks nowrap ids={r.rules} names={s.rule_names} />,
					},
				];
				return (
					<Card>
						<CardContent className="space-y-3 py-4">
							<p className="text-xs text-text-muted">
								每轮召回前按当前客户名下的订单 ／
								商品算（逐单算，取不到记未知、不记否），召回、准入、知识推送、指南条件、执行前置都用这一份。
								退货窗口默认 {s.window.days} 天，店铺参数里另写的按店算；有窗口的店：
								{(s.window.store_names ?? s.window.stores).join("、") || "无"}（见{" "}
								<PageJump to="business-params" />
								）。使用次数按生效画像 <span className="font-mono">{s.profile_version}</span>。
							</p>
							<PlanTable
								columns={cols}
								rows={s.data_states}
								rowKey={(r) => r.key}
								onRowClick={setOpen}
								density="compact"
								rowTestId="plan-recall-state-row"
							/>
							<p className="text-xs text-text-muted">
								共 {s.data_states.length} 种 · 点一行看谁在用
							</p>
							<Drawer
								open={!!open}
								onClose={() => setOpen(null)}
								width="min(720px,95vw)"
								testId="plan-recall-state-drawer"
								title={
									<span>
										{open ? labelOf(STATE_LABEL, open.key) : ""}{" "}
										<span className="font-mono text-xs text-text-muted">{open?.key}</span>
									</span>
								}
								badges={open ? <Badge tone="neutral">{open.scope}</Badge> : null}
							>
								{open ? <StateDetail r={open} s={s} /> : null}
							</Drawer>
						</CardContent>
					</Card>
				);
			}}
		</WithSchema>
	);
}

function StateDetail({ r, s }: { r: RecallStateRow; s: RecallSchema }) {
	return (
		<>
			<DrawerSection title="怎么算">
				<KV k="来自字段">
					<span className="font-mono text-xs">{r.fields.join("、") || "—"}</span>
				</KV>
				<KV k="算法">{r.how}</KV>
				<KV k="规则">
					<RuleLinks ids={r.rules} names={s.rule_names} />
				</KV>
				{!r.produced ? (
					<p className="text-xs text-text-muted">
						取数这一层当前不产出这个状态：挂在它上面的召回路 ／ 条件永远不会因它命中。
					</p>
				) : null}
				{!r.in_enum ? (
					<p className="text-xs text-text-muted">
						不在画像的数据状态枚举里：工具画像、指南条件选不到它，只有知识条目能用。
					</p>
				) : null}
			</DrawerSection>
			<DrawerSection title="被谁用到" testId="plan-recall-state-uses">
				{(Object.keys(USE_LABEL) as (keyof RecallStateRow["used_by"])[]).map((k) => (
					<div key={k} className="space-y-1">
						<div className="flex flex-wrap items-baseline gap-x-2 text-xs text-text-muted">
							<span>{USE_LABEL[k]}</span>
							<RuleLinks ids={s.state_use_rules[k] ?? []} names={s.rule_names} />
						</div>
						<div className="flex flex-wrap gap-1.5">
							{r.used_by[k].length ? (
								r.used_by[k].map((x) => (
									<Badge key={x} tone="muted">
										<span className="font-mono">{x}</span>
									</Badge>
								))
							) : (
								<span className="text-sm text-text-muted">—</span>
							)}
						</div>
					</div>
				))}
				<p className="text-xs text-text-muted">
					知识条目正文在
					<Link to="/plan-lab" hash="knowledge" className={linkCls}>
						「政策知识」页
					</Link>
					，指南正文在
					<Link to="/plan-lab" hash="guidance" className={linkCls}>
						「应答指南」页
					</Link>
					。
				</p>
			</DrawerSection>
		</>
	);
}

/* ── 排序（按生效引擎）＋ 候选呈现分层 ───────────────────────── */

const ENGINE_LABEL: Record<string, string> = {
	jev: "Jev 重排服务（TypeSafe）",
	llm: "大模型（按同样题型判）",
};

export function RankPanel() {
	return (
		<WithSchema testId="plan-block-recall-rank">
			{(s) => (
				<div className="space-y-4">
					<EngineCard s={s} />
					<RankFlowCard s={s} />
					<FocusCard s={s} />
				</div>
			)}
		</WithSchema>
	);
}

function EngineCard({ s }: { s: RecallSchema }) {
	const { rank } = s;
	const j = rank.jev;
	const l = rank.llm;
	return (
		<Card data-testid="plan-recall-engine">
			<CardHeader>
				<CardTitle>排序引擎</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2.5">
				<KV k="当前引擎">
					<span data-testid="plan-recall-engine-name">
						{ENGINE_LABEL[rank.engine] ?? rank.engine}
					</span>{" "}
					<span className="text-xs text-text-muted">
						（生效配置 plan.rank.engine；切换在
						<Link to="/plan-lab" hash="prompts" className={linkCls}>
							「模型提示」页
						</Link>
						）
					</span>
				</KV>
				{j ? (
					<>
						<KV k="请求的版本">
							<span className="font-mono">{j.requested_model}</span>{" "}
							{j.locked ? null : (
								<Badge tone="warning">
									<span data-testid="plan-recall-jev-unlocked">未锁定版本</span>
								</Badge>
							)}
							{j.locked ? null : (
								<span className="ml-2 text-xs text-text-muted">
									服务端升级后实际版本会变，以下面实际返回的为准
								</span>
							)}
							{j.locked ? (
								<span className="text-xs text-text-muted" data-testid="plan-recall-jev-locked">
									已锁定（配置 {j.model_key ?? "plan.rank.jev.model"}，进配置快照，同一快照能复现）
								</span>
							) : null}
						</KV>
						<KV k="实际返回的版本">
							<span className="font-mono" data-testid="plan-recall-jev-seen">
								{j.seen_model ?? "—"}
							</span>{" "}
							<span className="text-xs text-text-muted">
								{j.seen_source === "process"
									? "本进程最近一次调用"
									: j.seen_source === "ledger"
										? `近 7 天账本里最近一次（${(j.seen_at ?? "").slice(0, 16).replace("T", " ")}）`
										: "服务启动后还没调过，近 7 天账本里也没有"}
							</span>
						</KV>
						<KV k="阈值">
							覆盖概率 ≥ {j.yes_at} 记适配，≤ {j.no_at} 记不适配，其间记拿不准
							<span className="ml-1 text-xs text-text-muted">
								（配置 {(j.threshold_keys ?? ["plan.rank.yes_at", "plan.rank.no_at"]).join(" ／ ")}
								）
							</span>
						</KV>
						<KV k="超时与失败">
							{j.timeout_s} 秒超时；没配 Key、调用失败、返回没答案 → 全部按拿不准走粗档，本轮照常
						</KV>
						<KV k="题面模板">
							<span className="text-xs text-text-muted">
								每个非常备候选一道是非题（配置 {j.question_key}）
							</span>
						</KV>
						<Pre text={j.question} testId="plan-recall-jev-question" />
					</>
				) : null}
				{l ? (
					<>
						<KV k="排序模型">
							<span className="font-mono" data-testid="plan-recall-rank-model">
								{l.model ?? "—"}
							</span>{" "}
							<span className="text-xs text-text-muted">（配置 {l.model_key}）</span>
						</KV>
						<KV k="rank 提示词">
							<span className="text-xs text-text-muted">system ／ user 两段，生效快照里的原文</span>
						</KV>
						<Pre text={l.system} testId="plan-recall-rank-system" />
						<Pre text={l.user} testId="plan-recall-rank-user" />
					</>
				) : null}
			</CardContent>
		</Card>
	);
}

/** 画像 pv4 起：相关性为主、意图加分（标签体系 v2 批 3） */
function RelevanceFlowCard({ s }: { s: RecallSchema }) {
	const n = s.rule_names;
	const jev = s.rank.engine === "jev";
	const tr = s.tool_recall;
	return (
		<Card data-testid="plan-recall-rank-flow">
			<CardHeader>
				<CardTitle>排序与分档（相关性为主、意图加分）</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2 text-sm text-text-secondary">
				<p>
					召回只「给全 ＋
					排序」：不判要不要工具、要不要先澄清、能不能办，也不判指南条件成不成立——这些交 N2 ／ N3。
				</p>
				<p>
					① 候选 <RuleLink id="REC-02" names={n} />
					：非常备工具（{tr?.non_standing} 个）全部进相关性判断，多于 {tr?.full_judge_max}{" "}
					个才先粗筛；前置工具记「是谁的前置」 <RuleLink id="REC-03" names={n} />。
				</p>
				<p>
					② 相关度 <RuleLink id={jev ? "REC-JEV" : "REC-07"} names={n} />：
					{jev
						? "每个候选交 Jev 一道是非题「描述是否覆盖这件具体的事」，概率即相关度。"
						: "排序模型对每个候选判是 ／ 否 ／ 拿不准，折成相关度 0.8 ／ 0.2 ／ 0.5。"}
				</p>
				<p>
					③ 意图加分 <RuleLink id="REC-08" names={n} />
					：诉求意图在工具意图标签里加 {tr?.intent_bonus}（配置 {tr?.intent_bonus_key}
					），只加分不剔除。
				</p>
				<p>
					④ 分档 <RuleLink id="REC-09" names={n} />
					：最终分 ≥ {tr?.yes_at} 推荐、≤ {tr?.no_at} 不推荐、其间可选；执行前置确定不满足 →
					不推荐（附原因，不删）；没判到 → 可选；推荐工具的前置不低于可选。
				</p>
				<p>
					⑤ 排序与首选 <RuleLink id="REC-12" names={n} />
					：按档位、是否前置、最终分排；第一个是推荐或可选才作首选——只是建议，N2 拍板。
				</p>
				<p>
					⑥ 失败兜底 <RuleLink id="REC-15" names={n} />：
					{jev
						? "Jev 失败全部按可选，意图标签命中的排前"
						: "输出不是 JSON 重试 1 次，仍失败全部按可选"}
					；超时等节点异常本轮不建事项，不作废整轮。
				</p>
			</CardContent>
		</Card>
	);
}

function RankFlowCard({ s }: { s: RecallSchema }) {
	return <RelevanceFlowCard s={s} />;
}

function FocusCard({ s }: { s: RecallSchema }) {
	return (
		<Card data-testid="plan-recall-focus">
			<CardHeader>
				<CardTitle>候选呈现分层（给 N2）</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2 text-sm text-text-secondary">
				<p>
					召回宁多勿少，候选集合与准入不变；只改给 N2 的呈现{" "}
					<RuleLinks ids={s.focus.rules} names={s.rule_names} />：
				</p>
				<p>
					· 全量说明（做什么 ＋ 能力边界 ＋ 用法 ＋ 要说出的选择）：召回判为推荐 ／
					可选档的工具、它们的前置工具、常备工具（
					<span className="font-mono text-xs">{s.focus.standing_tools.join("、") || "—"}</span>
					）；排在前面。
				</p>
				<p>· 一句话（只给「做什么」）：不推荐档的候选，排在后面。</p>
				<p>· 推荐 ／ 可选一个都没有 → 退回意图标签命中的；再没有 → 全部给全量。</p>
				<p>
					· 候选外目录：工具多于全量判断上限被粗筛掉的，以「编号 ＋
					一句话」挂在最后，漏打不让能力消失。
				</p>
			</CardContent>
		</Card>
	);
}

/* ── 指南召回五种来源 ─────────────────────────────────────── */

const SOURCE_TEXT: Record<RecallGuideSourceKey, { title: string; line: string }> = {
	by_relevance: {
		title: "相关性判断",
		line: "建事项时按每条指南的适用条件逐条判和这件事对不对得上（与工具排序同一次 Jev），相关度 ＋ 标签加分够进来线就召回；意图 ／ 情绪风险标签只加分，不挂意图 ／ 工具",
	},
	by_facts: {
		title: "按事实补",
		line: "带数据条件或信息项条件的：每步决策前、回复前按事项当前的订单状态与已知信息再补（签收超窗口、退货原因……）",
	},
	standing: { title: "常备", line: "不经召回，每轮都带给 N2 ／ N3" },
	idle: {
		title: "闲聊",
		line: "本轮没有激活的事项（纯寒暄）时召回给 N3（按本轮原话判相关、挂闲聊意图标签的加分）",
	},
};

export function GuideSourcesCard() {
	return (
		<WithSchema testId="plan-block-recall-guide-sources">
			{(s) => (
				<Card>
					<CardContent className="space-y-2 py-4">
						<div className="flex flex-wrap items-baseline gap-x-3">
							<span className="text-sm font-semibold text-text-primary">指南怎么进来</span>
							<span className="text-xs text-text-muted">
								生效 {s.guides.total_live} 条； 相关性判断 ／ 常备两者互斥
								，按事实补与闲聊是其中的一部分。正文、强度、注入预览在
								<Link
									to="/plan-lab"
									hash="guidance"
									className={linkCls}
									data-testid="plan-recall-guide-sources-link"
								>
									「应答指南」页
								</Link>
							</span>
						</div>
						{s.guide_recall ? <GuideRelevanceLine g={s.guide_recall} names={s.rule_names} /> : null}
						{s.guides.sources.map((src) => (
							<div
								key={src.key}
								className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm"
								data-testid="plan-recall-guide-source"
							>
								<span className="w-20 shrink-0 font-medium text-text-primary">
									{SOURCE_TEXT[src.key]?.title ?? src.key}
								</span>
								<span className="w-12 shrink-0 font-mono text-xs text-text-secondary">
									{src.guides.length} 条
								</span>
								<span className="min-w-0 flex-1 text-text-secondary">
									{SOURCE_TEXT[src.key]?.line}
									{src.key === "idle" && s.guides.idle_intent
										? `（意图：${s.guides.idle_intent_name}）`
										: ""}
								</span>
								<RuleLinks ids={src.rules} names={s.rule_names} />
							</div>
						))}
					</CardContent>
				</Card>
			)}
		</WithSchema>
	);
}

/* ── 知识推送 ─────────────────────────────────────────────── */

type Constraint = RecallSchema["knowledge"]["constraints"][number];

export function KnowledgePanel() {
	return (
		<WithSchema testId="plan-block-recall-knowledge">
			{(s) => {
				const k = s.knowledge;
				const n = s.rule_names;
				const cols: PlanColumn<Constraint>[] = [
					{
						key: "id",
						title: "知识条目",
						className: "max-w-[200px]",
						render: (c) => <span className="block truncate font-mono text-xs">{c.id}</span>,
					},
					{
						key: "title",
						title: "标题",
						className: "max-w-[200px]",
						render: (c) => (
							<span className="block truncate" title={c.title}>
								{c.title}
							</span>
						),
					},
					{
						key: "tools",
						title: "拦哪些写工具",
						render: (c) => <CellTags mono tone="warning" items={c.tools} />,
					},
					{
						key: "states",
						title: "约束时的数据状态",
						render: (c) => <CellTags items={c.data_states.map((x) => labelOf(STATE_LABEL, x))} />,
					},
					{
						key: "unless",
						title: "例外（信息项取值，按类展开）",
						className: "max-w-[220px]",
						render: (c) => {
							const t = Object.entries(c.unless)
								.map(([slot, vs]) => `${slot} ∈ ${vs.join("、")}`)
								.join("；");
							return (
								<span className="block truncate text-text-secondary" title={t}>
									{t || "—"}
								</span>
							);
						},
					},
				];
				return (
					<div className="space-y-4">
						<Card>
							<CardHeader>
								<CardTitle>按条件推送店铺知识</CardTitle>
							</CardHeader>
							<CardContent className="space-y-2 text-sm text-text-secondary">
								{s.guide_recall ? (
									<p data-testid="plan-recall-knowledge-relevance">
										<RuleLink id="KNO-01" names={n} />
										：每步决策前与回复前，只看客户所属店铺适用的条目（准入）；限数据状态的要事项相关订单（绑定的＋查到过的）当前状态与之有交集（系统事实，还没查到单先不推）；再按建事项时逐条判的相关度
										＋ 意图标签加分（{s.guide_recall.know_intent_bonus}）≥ 进来线{" "}
										{s.guide_recall.know_min} 推给 N2 ／ N3（
										<RuleLink id="REC-REL" names={n} />
										，意图不再是卡口）。店铺政策块在指南块前。
									</p>
								) : null}
								<p>
									上线条目 {k.total} 条，其中 {k.with_states.length}{" "}
									条限数据状态。客户没问政策、但办这件事要受政策约束时（超窗口退款、已在退货流程又要退款），靠推送让
									N2 看得到。
								</p>
								<p>
									<RuleLink id="KNO-02" names={n} />
									：知识检索工具按问题查同一份条目，有 Jev Key 时 Jev 判「能回答这个问题」，概率 ≥{" "}
									{k.search.jev_min} 的按概率排，否则按关键词；最多 {k.search.top} 条。
								</p>
								<p className="text-xs text-text-muted">
									条目正文、适用店铺、标签在
									<Link
										to="/plan-lab"
										hash="knowledge"
										className={linkCls}
										data-testid="plan-recall-knowledge-link"
									>
										「政策知识」页
									</Link>
									看。
								</p>
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle>约束：执行前拦写操作</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								<p className="text-sm text-text-secondary">
									<RuleLink id="KNO-03" names={n} />：
									客户所属店铺的条目里（不经相关度：本次写的那张订单状态对得上就核）
									带约束、约束的正是这个写工具、例外不成立 → 不提交，政策原文作为原因交还
									N2。组参前核一次，客户确认后、真执行前再核一次。
								</p>
								<PlanTable
									columns={cols}
									rows={k.constraints}
									rowKey={(c) => c.id}
									density="compact"
									rowTestId="plan-recall-constraint-row"
								/>
								<p className="text-xs text-text-muted">共 {k.constraints.length} 条带约束</p>
							</CardContent>
						</Card>
					</div>
				);
			}}
		</WithSchema>
	);
}

/** 指南 ／ 知识相关性判断的参数（标签体系 v2 批 4，画像 pv5 起）：进来线、加分、每件诉求最多问几条、题面 */
function GuideRelevanceLine({
	g,
	names,
}: {
	g: NonNullable<RecallSchema["guide_recall"]>;
	names: Record<string, string>;
}) {
	return (
		<div
			className="space-y-1 rounded-md border border-surface-border bg-surface-muted/40 p-3 text-xs text-text-secondary"
			data-testid="plan-recall-guide-relevance"
		>
			<div>
				<RuleLink id="REC-REL" names={names} />
				：指南最终分 ＝ 相关度 ＋ 意图加分 {g.guide_intent_bonus}（诉求意图在指南意图标签里）＋
				情绪与风险加分 {g.signal_bonus}（本轮信号命中指南情绪与风险标签）≥ 进来线 {g.guide_min}{" "}
				召回；知识 ＝ 相关度 ＋ 意图加分 {g.know_intent_bonus} ≥ {g.know_min}{" "}
				推送。每件诉求每类最多问 {g.judge_max}{" "}
				条。相关度在建事项时判一次、按事项缓存，之后每步只按当前事实重算硬条件与加分。Jev
				没判到时退回意图标签。
			</div>
			<div>
				配置项：<span className="font-mono">{Object.values(g.keys).join("、")}</span>（改在{" "}
				<PageJump to="config-versions" />
				）；题面 <span className="font-mono">{g.question_keys.join("、")}</span>（改在{" "}
				<PageJump to="prompts" />）
			</div>
			<div>
				本轮情绪与风险旗（信号给出，只作参考、命中指南标签加分）见 <PageJump to="signal" />
			</div>
			<div className="text-text-muted">指南题面：{g.guide_question}</div>
			<div className="text-text-muted">知识题面：{g.knowledge_question}</div>
		</div>
	);
}

/** 召回评测入口：一句话 ＋ 跳 #recall-eval */
export function RecallEvalCard() {
	return (
		<Card data-testid="plan-recall-eval-entry">
			<CardContent className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 text-sm">
				<span className="font-medium text-text-primary">召回评测</span>
				<span className="text-text-secondary">
					召回这一层单独评：逐轮金标回放，看工具与指南的召回率、精准率，坏例下钻到候选表。
				</span>
				<Link
					to="/plan-lab"
					hash="recall-eval"
					className={`ml-auto ${linkCls}`}
					data-testid="plan-recall-eval-link"
				>
					去召回评测
				</Link>
			</CardContent>
		</Card>
	);
}
